import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiGet } from "@/lib/api";
import { fmtEUR, initials } from "@/lib/format";
import { StatusBadge, ContactBadge } from "@/components/StatusBadge";
import { OwnerDialog } from "@/components/dialogs/OwnerDialog";
import { DogDialog } from "@/components/dialogs/DogDialog";
import { DataUnavailable } from "@/components/pwa/OfflineBanner";
import { useResource } from "@/pwa/useResource";
import { useOnlineGuard } from "@/pwa/useOnlineGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Search, Dog as DogIcon, Coins, Phone } from "lucide-react";

const CONTACT_FILTERS = [
  ["Da ricontattare", "Da ricontattare"], ["Da conoscere", "Da conoscere"],
  ["Occasionale", "Occasionale"], ["Chiuso", "Chiusi"], ["all", "Tutti"],
];

export default function ClientsDogs() {
  const [q, setQ] = useState("");
  const [view, setView] = useState("clienti"); // clienti | contatti
  const [statusFilter, setStatusFilter] = useState("Attivo");
  const [contactFilter, setContactFilter] = useState("all");
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [dogOpen, setDogOpen] = useState(false);
  const [newDogOwner, setNewDogOwner] = useState("");
  const nav = useNavigate();

  const { data: rd, unavailable, reload: load } = useResource(useCallback(async () => {
    const [d, o] = await Promise.all([apiGet("/dogs"), apiGet("/owners")]);
    return { data: { dogs: d.data, owners: o.data }, stale: d.stale || o.stale };
  }, []), []);
  const dogs = rd?.dogs || [];
  const owners = rd?.owners || [];
  const { guard } = useOnlineGuard();

  const ql = q.toLowerCase();
  const stageOf = (o) => o.contact_stage || "Cliente";
  const ownerStageById = Object.fromEntries(owners.map((o) => [o.id, stageOf(o)]));
  const dogNamesOf = (oid) => dogs.filter((d) => d.owner_id === oid).map((d) => d.nome);

  const statusMatch = (rec) => statusFilter === "all" || rec.stato === statusFilter;
  const isClient = (o) => stageOf(o) === "Cliente";

  // Clienti view (existing experience, restricted to real clients).
  const fdogs = dogs.filter((d) => (ownerStageById[d.owner_id] || "Cliente") === "Cliente"
    && statusMatch(d) && (d.nome.toLowerCase().includes(ql) || (d.owner_name || "").toLowerCase().includes(ql)));
  const fowners = owners.filter((o) => isClient(o) && statusMatch(o)
    && (`${o.nome} ${o.cognome}`.toLowerCase().includes(ql) || (o.telefono || "").includes(q)));

  // Contatti view.
  const fcontacts = owners.filter((o) => {
    const st = stageOf(o);
    if (st === "Cliente") return false;
    if (contactFilter !== "all" && st !== contactFilter) return false;
    return `${o.nome} ${o.cognome}`.toLowerCase().includes(ql)
      || (o.telefono || "").includes(q)
      || (o.email || "").toLowerCase().includes(ql)
      || dogNamesOf(o.id).some((n) => n.toLowerCase().includes(ql));
  });

  const dogsEmpty = statusFilter === "Concluso" ? "Nessun cane archiviato trovato." : statusFilter === "Attivo" ? "Nessun cane attivo trovato." : "Nessun cane trovato.";
  const ownersEmpty = statusFilter === "Concluso" ? "Nessun cliente archiviato trovato." : statusFilter === "Attivo" ? "Nessun cliente attivo trovato." : "Nessun cliente trovato.";
  const STATUS_OPTS = [["Attivo", "Attivi"], ["Concluso", "Archiviati"], ["all", "Tutti"]];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Clienti e cani</h1>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="add-owner-btn" onClick={guard(() => setOwnerOpen(true))} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Cliente</Button>
          <Button data-testid="add-dog-btn" onClick={guard(() => { setNewDogOwner(""); setDogOpen(true); })} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Cane</Button>
        </div>
      </div>

      {/* View switch */}
      <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="view-switch">
        {[["clienti", "Clienti"], ["contatti", "Contatti"]].map(([val, label]) => (
          <button key={val} data-testid={`view-${val}`} onClick={() => setView(val)}
            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="cd-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={view === "contatti" ? "Cerca contatto, cane, telefono…" : "Filtra per nome…"} className="pl-9 h-11 rounded-full" />
        </div>
        {view === "clienti" ? (
          <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="status-filter">
            {STATUS_OPTS.map(([val, label]) => (
              <button key={val} data-testid={`status-${val}`} onClick={() => setStatusFilter(val)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${statusFilter === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
            ))}
          </div>
        ) : (
          <div className="inline-flex flex-wrap rounded-full border border-border bg-card p-1" data-testid="contact-filter">
            {CONTACT_FILTERS.map(([val, label]) => (
              <button key={val} data-testid={`contact-${val}`} onClick={() => setContactFilter(val)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${contactFilter === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {unavailable ? <DataUnavailable /> : view === "clienti" ? (
        <Tabs defaultValue="cani">
          <TabsList>
            <TabsTrigger value="cani" data-testid="tab-cani">Cani ({fdogs.length})</TabsTrigger>
            <TabsTrigger value="clienti" data-testid="tab-clienti">Clienti ({fowners.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="cani" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="dogs-grid">
              {fdogs.map((d) => (
                <button key={d.id} data-testid={`dog-card-${d.id}`} onClick={() => nav(`/cani/${d.id}`)}
                  className={`text-left rounded-2xl border border-border bg-card p-4 hover:border-primary transition-colors ${d.stato === "Concluso" ? "opacity-70" : ""}`}>
                  <div className="flex items-start gap-3">
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-primary/10 text-primary font-bold shrink-0">{initials(d.nome)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="font-head font-bold break-words min-w-0">{d.nome}</span>
                        {d.tipo_caso === "Cane personale" && <span className="text-xs rounded-full bg-secondary px-2 py-0.5 font-semibold">Personale</span>}
                      </div>
                      <div className="text-sm text-muted-foreground break-words">{d.razza || "—"} · {d.owner_name}</div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <StatusBadge value={d.stato} />
                        {d.crediti_residui > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary"><Coins className="h-3.5 w-3.5" />{d.crediti_residui} crediti</span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
              {fdogs.length === 0 && <p className="text-sm text-muted-foreground col-span-2" data-testid="dogs-empty">{dogsEmpty}</p>}
            </div>
          </TabsContent>

          <TabsContent value="clienti" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="owners-grid">
              {fowners.map((o) => (
                <button key={o.id} data-testid={`owner-card-${o.id}`} onClick={() => nav(`/clienti/${o.id}`)}
                  className={`text-left rounded-2xl border border-border bg-card p-4 hover:border-primary transition-colors ${o.stato === "Concluso" ? "opacity-70" : ""}`}>
                  <div className="flex items-start gap-3">
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-accent/15 text-accent font-bold shrink-0">{initials(`${o.nome} ${o.cognome}`)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-head font-bold break-words">{o.nome} {o.cognome}</div>
                      <div className="text-sm text-muted-foreground">{o.telefono || "—"}</div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <StatusBadge value={o.stato} />
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><DogIcon className="h-3.5 w-3.5" />{o.dogs_count} cani</span>
                        {o.outstanding > 0 && <span className="text-xs font-semibold text-accent">Da incassare {fmtEUR(o.outstanding)}</span>}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
              {fowners.length === 0 && <p className="text-sm text-muted-foreground col-span-2" data-testid="owners-empty">{ownersEmpty}</p>}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="contacts-grid">
          {fcontacts.map((o) => (
            <button key={o.id} data-testid={`contact-card-${o.id}`} onClick={() => nav(`/clienti/${o.id}`)}
              className="text-left rounded-2xl border border-border bg-card p-4 hover:border-primary transition-colors">
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#F3E5F5] text-[#6A1B9A] font-bold shrink-0">{initials(`${o.nome} ${o.cognome}`)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-head font-bold">{o.nome} {o.cognome}</div>
                  <div className="text-sm text-muted-foreground break-words">{dogNamesOf(o.id).join(", ") || "Nessun cane"}</div>
                  {o.telefono && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" />{o.telefono}</div>}
                  <div className="mt-2"><ContactBadge value={stageOf(o)} /></div>
                </div>
              </div>
            </button>
          ))}
          {fcontacts.length === 0 && <p className="text-sm text-muted-foreground col-span-2" data-testid="contacts-empty">Nessun contatto trovato.</p>}
        </div>
      )}

      <OwnerDialog open={ownerOpen} onOpenChange={setOwnerOpen} onSaved={load} onCreatedAddDog={(o) => { setNewDogOwner(o.id); setTimeout(() => setDogOpen(true), 220); }} />
      <DogDialog open={dogOpen} onOpenChange={(v) => { setDogOpen(v); if (!v) setNewDogOwner(""); }} fixedOwnerId={newDogOwner || undefined} onSaved={load} />
    </div>
  );
}
